/* Bridge to the native shell.
 *
 * The bridge carries intent and signals only — never entitlement. Anything
 * running in this WebView can post a message, so a "purchase succeeded" reply
 * is treated as a cue to re-ask the API, not as proof of anything.
 *
 * Everything here is a no-op in a normal browser, so callers do not need to
 * branch before using it. */

type Envelope = {
  v: number;
  id: string | null;
  type: string;
  payload?: any;
};

type NativeInfo = {
  bridgeVersion?: number;
  platform?: string;
  purchasesAvailable?: boolean;
  modelName?: string;
};

const NATIVE_EVENT = "lf-native";
const DEFAULT_TIMEOUT_MS = 90000;

const win = () => window as any;

const nativeInfo = (): NativeInfo | undefined => win().__lfNative;

/** True inside any native shell, including ones that predate the bridge. */
export const isNative = (): boolean => !!win().ReactNativeWebView;

/** 0 when the shell is too old to have announced itself. */
export const bridgeVersion = (): number => nativeInfo()?.bridgeVersion ?? 0;

/**
 * Whether the shell has a store SDK configured for its platform.
 *
 * Absent means yes. Every shell that shipped before this field existed does have
 * a key — reading a missing field as "no" would take the paywall away from every
 * iOS customer already on the App Store the moment this deploys. Only an explicit
 * `false` disables anything.
 */
export const nativePurchasesAvailable = (): boolean =>
  nativeInfo()?.purchasesAvailable !== false;

/* Branch on the version, never on isNative(). A Hosting deploy reaches shells
   that shipped before the bridge existed, and those must be asked to update
   rather than offered a purchase they cannot complete.

   The capability check is separate from the version check because the two mean
   different things to the customer: an old shell needs updating, while a shell
   without a key for its platform cannot sell anything however new it is. */
export const supportsNativePurchase = (): boolean =>
  bridgeVersion() >= 1 && nativePurchasesAvailable();

const pending = new Map<string, { resolve: (v: any) => void; timer: number }>();
const listeners = new Map<string, Set<(payload: any) => void>>();

const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const emit = (type: string, payload: any) => {
  listeners.get(type)?.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error(`native listener for ${type} failed:`, err);
    }
  });
};

const handleNativeMessage = (event: Event) => {
  let message: Envelope;
  try {
    message = JSON.parse((event as MessageEvent).data);
  } catch {
    return;
  }
  if (!message?.type) return;

  /* Replies are matched by correlation id; the channel gives no ordering. */
  if (message.id && pending.has(message.id)) {
    const entry = pending.get(message.id)!;
    window.clearTimeout(entry.timer);
    pending.delete(message.id);
    entry.resolve(message.payload);
  }

  emit(message.type, message.payload);
};

if (typeof window !== "undefined") {
  window.addEventListener(NATIVE_EVENT, handleNativeMessage);
}

/** Subscribe to a native-initiated message. Returns an unsubscribe function. */
export const onNative = (
  type: string,
  handler: (payload: any) => void
): (() => void) => {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type)!.add(handler);
  return () => {
    listeners.get(type)?.delete(handler);
  };
};

/* Resolves with { status: "unavailable" } rather than rejecting, so callers can
   treat "no bridge" and "bridge says no" the same way. A timeout also resolves
   as pending rather than failing: a purchase can settle minutes later, and
   treating silence as failure is what makes people buy twice. */
export const request = (
  type: string,
  payload: any = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<any> => {
  if (!supportsNativePurchase()) {
    return Promise.resolve({ status: "unavailable" });
  }
  return new Promise((resolve) => {
    const id = newId();
    const timer = window.setTimeout(() => {
      pending.delete(id);
      resolve({ status: "pending", reason: "timeout" });
    }, timeoutMs);

    pending.set(id, { resolve, timer });
    const envelope: Envelope = { v: 1, id, type, payload };
    try {
      win().ReactNativeWebView.postMessage(JSON.stringify(envelope));
    } catch (err) {
      window.clearTimeout(timer);
      pending.delete(id);
      resolve({ status: "failed", reason: String(err) });
    }
  });
};

/* Convenience wrappers — these names are the contract the shell implements. */
export const identifyUser = (firebaseUid: string) =>
  request("user.identify", { firebaseUid });

export const signOutNative = () => request("user.signout");

export const fetchCatalog = () => request("purchase.catalog", {}, 20000);

export const startPurchase = (productId: string) =>
  request("purchase.start", { productId });

export const restorePurchases = () => request("purchase.restore");
